"use client";

import { IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useEventCart } from "@/features/analytics/hooks/use-event-cart";
import { useEventCoupon } from "@/features/analytics/hooks/use-event-coupon";
import { getTotalPricingGtmValue } from "@/features/analytics/lib/gtm-value";
import type { CouponSourceType } from "@/features/analytics/types/event.type";
import type { AddDiscountReturnType } from "@/features/cart/api/add-discount";
import { getCartTotalPricing } from "@/features/cart/helpers/cart-total-pricing";
import { toProductItemInputs } from "@/features/cart/helpers/product-items";
import { useCartStoreService } from "@/features/cart/hooks/use-cart-store-service";
import type { Cart } from "@/features/cart/types";
import type { CartStoreType } from "@/features/cart/types/cart-store-type";

const BETTER_DISCOUNT_ERROR =
  "code_was_not_used_because_other_discounts_are_better";

// The code is valid, but only in the mobile app.
const WEB_NOT_SUPPORTED_ERROR = "web_not_supported";

const toastConfig = {
  cancel: (
    <Button
      className="ml-auto h-4 min-h-0 p-1"
      onClick={() => toast.dismiss()}
      size="icon"
      type="button"
      variant="ghost"
    >
      <IconX className="size-5" />
    </Button>
  ),
  closeButton: false,
  position: "top-right",
} as const;

const showDiscountErrorToast = (
  t: ReturnType<typeof useTranslations>,
  err: {
    message?: string;
    minimumAmount?: number | null;
    minimalDays?: number | null;
    translatedMessage?: string | null;
  },
) => {
  // An app-only code is not a mistake of the user: the coupon list offers the
  // app, so the message is informational (MPL-3137).
  const showToast =
    err?.message === WEB_NOT_SUPPORTED_ERROR ? toast.info : toast.error;

  if (err?.translatedMessage) {
    showToast(err.translatedMessage, toastConfig);
    return;
  }
  if (err?.minimumAmount != null) {
    showToast(
      t(`diets.discountErrors.${err.message}`, { amount: err.minimumAmount }),
      toastConfig,
    );
    return;
  }
  if (err?.minimalDays != null) {
    showToast(
      t(`diets.discountErrors.${err.message}`, {
        minimalDays: err.minimalDays,
      }),
      toastConfig,
    );
    return;
  }
  if (err?.message) {
    showToast(t(`diets.discountErrors.${err.message}`), toastConfig);
  }
};

export type AddDiscountErrorDetails = {
  error: string;
  message: string;
  minimumAmount: number | null;
  minimalDays: number | null;
  translatedMessage: string | null;
};

export const useAddDiscount = (type?: CartStoreType) => {
  const t = useTranslations();
  const { cartStore } = useCartStoreService({ type });
  const { sendCertEvent } = useEventCart();
  const { sendCouponAddedEvent, sendCouponRemovedEvent } = useEventCoupon();

  const [isError, setIsError] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] =
    useState<AddDiscountErrorDetails | null>(null);
  // The last typed code rejected as app-only, offered in the coupon list.
  const [mobileOnlyCode, setMobileOnlyCode] = useState<string | null>(null);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!cartStore.discountCode) {
      setInfo(null);
    }
  }, [cartStore.discountCode]);

  const handleRemove = useCallback(async () => {
    if (!cartStore.cartId) return;
    setInfo(null);
    setIsRemoveLoading(true);

    const removedCoupon = cartStore.discountCode;
    const cartItems = cartStore.items;
    const discounts = cartStore.pricing?.discounts ?? [];

    try {
      const res = await fetch("/api/cart/remove-discount", {
        method: "POST",
        body: JSON.stringify({
          cartId: cartStore.cartId,
          items: cartItems.map((item) => item.configurationItem),
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to remove discount");
      }

      if (removedCoupon) {
        sendCouponRemovedEvent({
          coupon: removedCoupon,
          cartId: cartStore.cartId,
          items: cartItems,
          discounts,
        });
      }

      const data = (await res.json()) as {
        cartId: string;
        cart: Cart;
      };
      await cartStore.updateCart(data.cartId, data.cart, "removeDiscount", t);
    } finally {
      setIsRemoveLoading(false);
    }
  }, [cartStore, t, sendCouponRemovedEvent]);

  const handleSuccessResponse = useCallback(
    (data: AddDiscountReturnType, setInfoState: (v: string | null) => void) => {
      if (data.error) {
        setInfoState(data.error);
        return data.error !== BETTER_DISCOUNT_ERROR;
      }
      setInfoState("code_added");
      return true;
    },
    [],
  );

  const addDiscount = useCallback(
    async (discountCode: string, source: CouponSourceType) => {
      const code = discountCode.trim();
      setIsError(false);
      setInfo(null);
      setErrorDetails(null);
      setMobileOnlyCode(null);
      setIsPending(true);

      if (!cartStore.cartId) {
        setIsPending(false);
        return;
      }
      if (!code) {
        setIsPending(false);
        return;
      }
      try {
        const res = await fetch("/api/cart/add-discount", {
          method: "POST",
          body: JSON.stringify({
            cartId: cartStore.cartId,
            discountCode: code,
            items: cartStore.items.map((item) => item.configurationItem),
            productItems: toProductItemInputs(cartStore.productItems),
          }),
        });

        if (!res.ok) {
          const error = (await res.json()) as AddDiscountErrorDetails;
          setIsError(true);
          setErrorDetails(error);
          if (error.message === WEB_NOT_SUPPORTED_ERROR) {
            setMobileOnlyCode(code);
          }
          sendCouponAddedEvent({
            coupon: code,
            status: "fail",
            source,
            cartId: cartStore.cartId,
            link_text: error.message ?? "unknown",
            items: cartStore.items,
            discounts: cartStore.pricing?.discounts ?? [],
          });
          throw error;
        }

        const data = (await res.json()) as AddDiscountReturnType & {
          cartId: string;
          cartRecreated?: boolean;
        };

        // The cart may have been recreated server-side (old one was finalized),
        // so always trust the cartId returned by the API over the stale one.
        const activeCartId = data.cartId ?? cartStore.cartId;

        // Priced from the re-quoted cart the API just returned, not from the
        // store: the store still holds the pre-coupon quote at this point, so
        // its amounts would report the discount as if it had not been applied
        // (MPL-3873).
        sendCertEvent({
          eventName: "view_cart",
          diets: data.cart.items.map((item) => ({
            ...item.configurationItem,
            price:
              (item.pricing.final.gross || 0) /
              (item.configurationItem.dates.length || 1),
            quantity: 1,
            duration: item.configurationItem.dates.length,
            subItemsPricing: item.subItemsPricing,
          })),
          value: getTotalPricingGtmValue({
            totalPricing: getCartTotalPricing(data.cart),
            items: data.cart.items,
            productItems: data.cart.productItems,
          }),
          other: { coupon: code, cartId: activeCartId },
        });

        sendCouponAddedEvent({
          coupon: data.cart.discountCode ?? code,
          status: "success",
          source,
          cartId: activeCartId,
          items: data.cart.items,
          discounts: data.cart.pricing?.discounts ?? [],
        });

        const shouldUpdateCart = handleSuccessResponse(data, setInfo);
        if (shouldUpdateCart) {
          await cartStore.updateCart(activeCartId, data.cart, "addDiscount", t);
        }
      } catch (error: unknown) {
        const err = error as AddDiscountErrorDetails;
        showDiscountErrorToast(t, err);
        setIsError(true);
      } finally {
        setIsPending(false);
      }
    },
    [cartStore, t, sendCertEvent, sendCouponAddedEvent, handleSuccessResponse],
  );

  return {
    addDiscount,
    handleRemove,
    isError,
    info,
    isRemoveLoading,
    isPending,
    errorDetails,
    mobileOnlyCode,
  };
};
